import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { useFormatting } from '@ayeldo/frontend-core/hooks/useFormatting';

import { useIdpSession } from '../../hooks/useIdpSession';
import { APIError, idpApi, type SessionSummary } from '../../services/idp-api';

function getSessionsErrorMessage(error: unknown, t: TFunction<'idp'>): string {
  if (!(error instanceof APIError)) {
    return t('sessions.errors.default');
  }

  return error.message;
}

export function SessionsPage() {
  const { t } = useTranslation('idp');
  const navigate = useNavigate();
  const { formatDateTime } = useFormatting();
  const { isAuthenticated, isLoading } = useIdpSession();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [busySessionId, setBusySessionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  async function loadSessions() {
    const response = await idpApi.listSessions();
    setSessions(response.data);
  }

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth/signin', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void (async () => {
      try {
        await loadSessions();
      } finally {
        setPageLoading(false);
      }
    })();
  }, [isAuthenticated]);

  async function handleRevoke(sessionId: string) {
    setErrorMessage(null);
    setSuccessMessage(null);
    setBusySessionId(sessionId);

    try {
      await idpApi.revokeSession(sessionId);
      await loadSessions();
      setSuccessMessage(t('sessions.revokeSuccess'));
    } catch (error) {
      setErrorMessage(getSessionsErrorMessage(error, t));
    } finally {
      setBusySessionId(null);
    }
  }

  if (isLoading || pageLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1" fontWeight={700}>
        {t('sessions.title')}
      </Typography>

      {successMessage !== null ? <Alert severity="success">{successMessage}</Alert> : null}
      {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Card>
        <CardContent>
          <List disablePadding>
            {sessions.map((session) => (
              <ListItem
                key={session.sessionId}
                disableGutters
                secondaryAction={
                  session.current ? undefined : (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => {
                        void handleRevoke(session.sessionId);
                      }}
                      disabled={busySessionId === session.sessionId}
                    >
                      {busySessionId === session.sessionId
                        ? t('sessions.revokingButton')
                        : t('sessions.revokeButton')}
                    </Button>
                  )
                }
              >
                <ListItemText
                  primary={`${session.authMethod === 'google' ? t('sessions.authMethodGoogle') : t('sessions.authMethodPassword')}${session.current ? t('sessions.currentSuffix') : ''}`}
                  secondary={`${t('sessions.signedIn', { date: formatDateTime(session.authTime) })} • ${t('sessions.lastActive', { date: formatDateTime(session.lastActivityAt) })}`}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Link component={RouterLink} to="/account" underline="hover">
        {t('sessions.backToDashboard')}
      </Link>
    </Stack>
  );
}
